import { getTexturePatternTile, isTexturePatternId } from "./texturePatterns";

import type {
  ElementBlur,
  ElementEffects,
  ElementNoise,
  ElementShadow,
  ElementTexture,
  ElementTextureBlendMode,
  ElementTextureFit,
  ExcalidrawElement,
  FileId,
} from "./types";

/** Minimal structural view of App's imageCache — a `Map<FileId, {image}>`
 *  satisfies this without importing across the package boundary. */
export type TextureImageCache = {
  get(
    fileId: FileId,
  ): { image: HTMLImageElement | Promise<unknown> } | undefined;
};

export const DEFAULT_DROP_SHADOW: ElementShadow = {
  color: "#00000066",
  offsetX: 0,
  offsetY: 4,
  blur: 10,
  spread: 0,
  enabled: true,
};

export const DEFAULT_INNER_SHADOW: ElementShadow = {
  color: "#00000066",
  offsetX: 0,
  offsetY: 4,
  blur: 10,
  spread: 0,
  enabled: true,
};

export const DEFAULT_BLUR: ElementBlur = {
  // Figma's default — radius 10 visually erases hairline strokes
  radius: 4,
  enabled: true,
};

export const DEFAULT_NOISE: ElementNoise = {
  intensity: 20,
  size: 1,
  enabled: true,
};

export const DEFAULT_TEXTURE: ElementTexture = {
  // a built-in so adding the effect does something immediately — the old
  // default (no source) rendered nothing until you uploaded an image
  pattern: "paper",
  fileId: null,
  fit: "tile",
  scale: 1,
  rotation: 0,
  offsetX: 0,
  offsetY: 0,
  color: "#000000",
  blendMode: "multiply",
  opacity: 45,
  enabled: true,
};

const TEXTURE_FITS: ElementTextureFit[] = ["tile", "stretch", "fill", "fit"];
const TEXTURE_FIT_SET = new Set<string>(TEXTURE_FITS);

export const TEXTURE_BLEND_MODES: readonly ElementTextureBlendMode[] = [
  "normal",
  "multiply",
  "screen",
  "overlay",
  "darken",
  "lighten",
  "color-burn",
  "color-dodge",
  "soft-light",
  "hard-light",
  "difference",
  "exclusion",
  "hue",
  "saturation",
  "color",
  "luminosity",
];

const BLEND_MODE_SET = new Set<string>(TEXTURE_BLEND_MODES);

const clampNonNegative = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 0;

const finiteOrZero = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const normalizeShadow = (shadow: unknown): ElementShadow | undefined => {
  if (!shadow || typeof shadow !== "object") {
    return undefined;
  }
  const s = shadow as Record<string, unknown>;
  return {
    color: typeof s.color === "string" ? s.color : DEFAULT_DROP_SHADOW.color,
    offsetX: finiteOrZero(s.offsetX),
    offsetY: finiteOrZero(s.offsetY),
    blur: clampNonNegative(s.blur),
    spread: clampNonNegative(s.spread),
    enabled: !!s.enabled,
  };
};

const normalizeNoise = (noise: unknown): ElementNoise | undefined => {
  if (!noise || typeof noise !== "object") {
    return undefined;
  }
  const n = noise as Record<string, unknown>;
  return {
    intensity: Math.min(100, clampNonNegative(n.intensity)),
    size: Math.max(1, clampNonNegative(n.size) || 1),
    enabled: !!n.enabled,
  };
};

const normalizeTexture = (texture: unknown): ElementTexture | undefined => {
  if (!texture || typeof texture !== "object") {
    return undefined;
  }
  const t = texture as Record<string, unknown>;
  return {
    pattern: isTexturePatternId(t.pattern) ? t.pattern : null,
    fileId: typeof t.fileId === "string" ? (t.fileId as FileId) : null,
    fit: TEXTURE_FIT_SET.has(t.fit as string)
      ? (t.fit as ElementTextureFit)
      : "stretch",
    scale: Math.max(0.01, clampNonNegative(t.scale) || 1),
    rotation: finiteOrZero(t.rotation),
    offsetX: finiteOrZero(t.offsetX),
    offsetY: finiteOrZero(t.offsetY),
    color: typeof t.color === "string" ? t.color : DEFAULT_TEXTURE.color,
    blendMode: BLEND_MODE_SET.has(t.blendMode as string)
      ? (t.blendMode as ElementTextureBlendMode)
      : "normal",
    opacity: Math.min(100, clampNonNegative(t.opacity)),
    enabled: !!t.enabled,
  };
};

const normalizeBlur = (blur: unknown): ElementBlur | undefined => {
  if (!blur || typeof blur !== "object") {
    return undefined;
  }
  const b = blur as Record<string, unknown>;
  return {
    radius: clampNonNegative(b.radius),
    enabled: !!b.enabled,
  };
};

/** Clamp/validate an untrusted effects payload (restore path). Returns null
 *  when nothing valid remains so old/clean elements stay `effects: null`. */
export const normalizeEffects = (effects: unknown): ElementEffects | null => {
  if (!effects || typeof effects !== "object") {
    return null;
  }
  const e = effects as Record<string, unknown>;
  const normalized: ElementEffects = {};
  const dropShadow = normalizeShadow(e.dropShadow);
  const innerShadow = normalizeShadow(e.innerShadow);
  const layerBlur = normalizeBlur(e.layerBlur);
  const backgroundBlur = normalizeBlur(e.backgroundBlur);
  const noise = normalizeNoise(e.noise);
  const texture = normalizeTexture(e.texture);
  if (dropShadow) {
    normalized.dropShadow = dropShadow;
  }
  if (innerShadow) {
    normalized.innerShadow = innerShadow;
  }
  if (layerBlur) {
    normalized.layerBlur = layerBlur;
  }
  if (backgroundBlur) {
    normalized.backgroundBlur = backgroundBlur;
  }
  if (noise) {
    normalized.noise = noise;
  }
  if (texture) {
    normalized.texture = texture;
  }
  return Object.keys(normalized).length ? normalized : null;
};

/** File referenced by an element's texture effect, if any. Persistence/cache
 *  code collects this alongside image `element.fileId`. Returned regardless of
 *  `enabled` so toggling a texture back on keeps its file. */
export const getTextureFileId = (element: ExcalidrawElement): FileId | null =>
  element.effects?.texture?.fileId ?? null;

export const hasEnabledEffects = (element: ExcalidrawElement): boolean => {
  const effects = element.effects;
  if (!effects) {
    return false;
  }
  return !!(
    effects.dropShadow?.enabled ||
    effects.innerShadow?.enabled ||
    effects.layerBlur?.enabled ||
    effects.backgroundBlur?.enabled ||
    effects.noise?.enabled ||
    (effects.texture?.enabled &&
      (effects.texture.fileId || effects.texture.pattern))
  );
};

let ctxFilterSupported: boolean | null = null;
/** `ctx.filter` is missing in older Safari — blurs no-op there, shadows
 *  still work (`ctx.shadow*` is universal). */
export const isCanvasFilterSupported = (): boolean => {
  if (ctxFilterSupported === null) {
    try {
      const ctx = document.createElement("canvas").getContext("2d");
      ctxFilterSupported = !!ctx && typeof ctx.filter === "string";
    } catch {
      ctxFilterSupported = false;
    }
  }
  return ctxFilterSupported;
};

/** Renders the shadow (only) of `source`'s alpha onto a new canvas of the
 *  same size, using the offset trick (source drawn off-canvas). */
const renderShadowOfAlpha = (
  source: HTMLCanvasElement,
  shadow: ElementShadow,
  scaleFactor: number,
): HTMLCanvasElement | null => {
  const { width, height } = source;
  const tmp = document.createElement("canvas");
  tmp.width = width;
  tmp.height = height;
  const tctx = tmp.getContext("2d");
  if (!tctx) {
    return null;
  }
  // shift the source fully off-canvas; shadow offsets are in device space
  // (unaffected by the CTM), so +OFF brings only the shadow back into view
  const OFF = width + height;
  tctx.shadowColor = shadow.color;
  tctx.shadowBlur = shadow.blur * scaleFactor;
  tctx.shadowOffsetX = shadow.offsetX * scaleFactor + OFF;
  tctx.shadowOffsetY = shadow.offsetY * scaleFactor;
  if (shadow.spread > 0) {
    // ponytail: spread approximated by scaling the silhouette about its
    // center — true spread needs a distance-field dilation
    const sx = (width + 2 * shadow.spread * scaleFactor) / width;
    const sy = (height + 2 * shadow.spread * scaleFactor) / height;
    tctx.translate(-OFF + width / 2, height / 2);
    tctx.scale(sx, sy);
    tctx.drawImage(source, -width / 2, -height / 2);
  } else {
    tctx.drawImage(source, -OFF, 0);
  }
  return tmp;
};

/** Post-processes a finished element cache canvas in place: drop shadow
 *  (under), inner shadow (atop), layer blur (whole). Element-type agnostic —
 *  operates purely on the canvas alpha. */
export const applyEffectsToCanvas = (
  canvas: HTMLCanvasElement,
  element: ExcalidrawElement,
  scaleFactor: number,
  imageCache?: TextureImageCache,
) => {
  const effects = element.effects;
  if (!effects || !canvas.width || !canvas.height) {
    return;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  const { width, height } = canvas;

  // texture + noise fill the element first, so shadows/blur layer over them
  // (drop shadow stays behind, inner shadow on top, layer blur covers all)
  if (effects.texture) {
    applyTexture(ctx, effects.texture, width, height, scaleFactor, imageCache);
  }
  if (effects.noise) {
    applyNoise(ctx, effects.noise, element.seed, width, height, scaleFactor);
  }

  const dropShadow = effects.dropShadow;
  if (dropShadow?.enabled) {
    const shadow = renderShadowOfAlpha(canvas, dropShadow, scaleFactor);
    if (shadow) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      ctx.drawImage(shadow, 0, 0);
      ctx.restore();
    }
  }

  const innerShadow = effects.innerShadow;
  if (innerShadow?.enabled) {
    // inverted alpha of the element…
    const inverse = document.createElement("canvas");
    inverse.width = width;
    inverse.height = height;
    const ictx = inverse.getContext("2d");
    if (ictx) {
      ictx.fillRect(0, 0, width, height);
      ictx.globalCompositeOperation = "destination-out";
      ictx.drawImage(canvas, 0, 0);
      // …whose shadow falls inside the shape; spread ≈ dilating the inverse
      const shadow = renderShadowOfAlpha(inverse, innerShadow, scaleFactor);
      if (shadow) {
        ctx.save();
        ctx.globalCompositeOperation = "source-atop";
        ctx.drawImage(shadow, 0, 0);
        ctx.restore();
      }
    }
  }

  const layerBlur = effects.layerBlur;
  if (layerBlur?.enabled && layerBlur.radius > 0 && isCanvasFilterSupported()) {
    const tmp = document.createElement("canvas");
    tmp.width = width;
    tmp.height = height;
    const tctx = tmp.getContext("2d");
    if (tctx) {
      tctx.filter = `blur(${layerBlur.radius * scaleFactor}px)`;
      tctx.drawImage(canvas, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = "copy";
      ctx.drawImage(tmp, 0, 0);
      ctx.restore();
    }
  }
};

/** The image a texture paints with: an uploaded file, else a built-in
 *  procedural tile. `fileId` wins so an upload isn't shadowed by a stale
 *  pattern id (the panel clears the other side when you switch source). */
const getTextureSource = (
  texture: ElementTexture,
  imageCache?: TextureImageCache,
): CanvasImageSource | null => {
  if (texture.fileId) {
    const img = imageCache?.get(texture.fileId)?.image;
    return !img || img instanceof Promise ? null : img;
  }
  return texture.pattern
    ? getTexturePatternTile(texture.pattern, texture.color)
    : null;
};

const sourceSize = (img: CanvasImageSource) => {
  const w = (img as HTMLImageElement).width;
  const h = (img as HTMLImageElement).height;
  return w && h ? { w, h } : null;
};

/** Lays the texture image over a `width`×`height` area honouring fit, scale,
 *  rotation and offset. Assumes the caller has already set composite/alpha
 *  and will restore the target's state. */
const paintTexture = (
  target: CanvasRenderingContext2D,
  texture: ElementTexture,
  img: CanvasImageSource,
  width: number,
  height: number,
  scaleFactor: number,
) => {
  const scale = texture.scale * scaleFactor;
  const offsetX = texture.offsetX * scaleFactor;
  const offsetY = texture.offsetY * scaleFactor;

  if (texture.fit === "tile") {
    const pattern = target.createPattern(img, "repeat");
    if (!pattern) {
      return;
    }
    // no setTransform (older Safari) → untransformed tiling, still correct
    pattern.setTransform?.(
      new DOMMatrix()
        .translate(offsetX, offsetY)
        .rotate(texture.rotation)
        .scale(scale),
    );
    target.fillStyle = pattern;
    target.fillRect(0, 0, width, height);
    return;
  }

  const size = sourceSize(img);
  if (!size) {
    return;
  }
  // "fill" covers the box (crops), "fit" is contained (letterboxes),
  // "stretch" ignores the source aspect ratio entirely
  const ratio =
    texture.fit === "fill"
      ? Math.max(width / size.w, height / size.h)
      : Math.min(width / size.w, height / size.h);
  const drawWidth =
    texture.fit === "stretch" ? width * scale : size.w * ratio * scale;
  const drawHeight =
    texture.fit === "stretch" ? height * scale : size.h * ratio * scale;
  target.translate(width / 2 + offsetX, height / 2 + offsetY);
  target.rotate((texture.rotation * Math.PI) / 180);
  target.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
};

/** Paints a texture over the element. Runs before shadows so the shadow
 *  isn't tinted.
 *
 *  A real blend mode (anything but "normal") has to go through an offscreen
 *  layer: blend operators composite as source-over, which would paint the
 *  texture outside the shape, so the layer is masked to the destination
 *  alpha first. "normal" — the common case — skips the allocation and paints
 *  straight onto the target with `composite`. Frames pass "source-over" and
 *  are never masked (their "shape" is the whole rect). */
const applyTexture = (
  ctx: CanvasRenderingContext2D,
  texture: ElementTexture,
  width: number,
  height: number,
  scaleFactor: number,
  imageCache?: TextureImageCache,
  composite: GlobalCompositeOperation = "source-atop",
) => {
  if (!texture.enabled) {
    return;
  }
  const img = getTextureSource(texture, imageCache);
  if (!img) {
    return;
  }
  const alpha = Math.min(1, texture.opacity / 100);

  if (texture.blendMode === "normal") {
    ctx.save();
    ctx.globalCompositeOperation = composite;
    ctx.globalAlpha = alpha;
    paintTexture(ctx, texture, img, width, height, scaleFactor);
    ctx.restore();
    return;
  }

  const layer = document.createElement("canvas");
  layer.width = width;
  layer.height = height;
  const lctx = layer.getContext("2d");
  if (!lctx) {
    return;
  }
  paintTexture(lctx, texture, img, width, height, scaleFactor);
  if (composite === "source-atop") {
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.globalCompositeOperation = "destination-in";
    lctx.drawImage(ctx.canvas, 0, 0);
  }

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = texture.blendMode;
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
};

const NOISE_TILE_SIZE = 256;
/** grain tiles keyed by element seed — generated once, pattern-repeated */
const noiseTileCache = new Map<number, HTMLCanvasElement>();

const getNoiseTile = (seed: number): HTMLCanvasElement | null => {
  const key = (seed || 1) >>> 0;
  const cached = noiseTileCache.get(key);
  if (cached) {
    return cached;
  }
  const tile = document.createElement("canvas");
  tile.width = NOISE_TILE_SIZE;
  tile.height = NOISE_TILE_SIZE;
  const tctx = tile.getContext("2d");
  if (!tctx) {
    return null;
  }
  const data = tctx.createImageData(NOISE_TILE_SIZE, NOISE_TILE_SIZE);
  let s = key; // LCG — use HIGH bits: the low bits have power-of-two periods
  // that align with the 256px tile width and render as vertical stripes
  for (let i = 0; i < data.data.length; i += 4) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const v = s >>> 24;
    data.data[i] = v;
    data.data[i + 1] = v;
    data.data[i + 2] = v;
    data.data[i + 3] = 255;
  }
  tctx.putImageData(data, 0, 0);
  if (noiseTileCache.size >= 64) {
    // ponytail: drop oldest insertion — full LRU unnecessary for grain tiles
    noiseTileCache.delete(noiseTileCache.keys().next().value!);
  }
  noiseTileCache.set(key, tile);
  return tile;
};

/** Overlays deterministic monochrome grain (seeded by the element so it
 *  doesn't flicker on re-cache), clipped to the shape's alpha. */
const applyNoise = (
  ctx: CanvasRenderingContext2D,
  noise: ElementNoise,
  seed: number,
  width: number,
  height: number,
  scaleFactor: number,
  composite: GlobalCompositeOperation = "source-atop",
) => {
  if (!noise.enabled || noise.intensity <= 0) {
    return;
  }
  const tile = getNoiseTile(seed);
  if (!tile) {
    return;
  }
  const block = Math.max(1, Math.round(noise.size * scaleFactor));
  ctx.save();
  ctx.globalCompositeOperation = composite;
  ctx.globalAlpha = Math.min(1, noise.intensity / 100);
  ctx.imageSmoothingEnabled = false;
  const pattern = ctx.createPattern(tile, "repeat");
  if (pattern) {
    // scale the tile so one noise cell = `block` device px
    pattern.setTransform?.(new DOMMatrix([block, 0, 0, block, 0, 0]));
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.restore();
};

/** Paints a frame's surface — background color, then texture, then noise —
 *  onto a rect-sized canvas. Unlike shape effects, a frame's "shape" is its
 *  whole rectangle, so this composites `source-over` (no alpha mask to clip
 *  against); a texture over a transparent frame blends with the scene behind. */
export const renderFrameSurface = (
  canvas: HTMLCanvasElement,
  element: ExcalidrawElement,
  scaleFactor: number,
  imageCache?: TextureImageCache,
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  const { width, height } = canvas;
  const bg = element.backgroundColor;
  if (bg && bg !== "transparent") {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);
  }
  const effects = element.effects;
  if (effects?.texture) {
    applyTexture(
      ctx,
      effects.texture,
      width,
      height,
      scaleFactor,
      imageCache,
      "source-over",
    );
  }
  if (effects?.noise) {
    applyNoise(
      ctx,
      effects.noise,
      element.seed,
      width,
      height,
      scaleFactor,
      "source-over",
    );
  }
};

/** Extra element-canvas padding (in scene px) needed so enabled effects
 *  don't clip. Inner/background blur render inside the shape → no padding. */
export const getEffectsPadding = (element: ExcalidrawElement): number => {
  const effects = element.effects;
  if (!effects) {
    return 0;
  }
  let padding = 0;
  const shadow = effects.dropShadow;
  if (shadow?.enabled) {
    padding = Math.max(
      padding,
      shadow.blur +
        shadow.spread +
        Math.max(Math.abs(shadow.offsetX), Math.abs(shadow.offsetY)),
    );
  }
  if (effects.layerBlur?.enabled) {
    padding = Math.max(padding, effects.layerBlur.radius * 2);
  }
  return Math.ceil(padding);
};
